import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import { patientService } from '../services/patientService';
import { testService } from '../services/testService';
import { userService } from '../services/userService';
import { LoadingOrb } from '../components/LoadingOrb';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [patientCount, setPatientCount] = useState<number | null>(null);
  const [evaluationCount, setEvaluationCount] = useState<number | null>(null);
  const [activeUserCount, setActiveUserCount] = useState<number | null>(null);

  useEffect(() => {
    let active = true;

    patientService.getPatients(1, 1)
      .then((res) => { if (active) setPatientCount(res.pagination.total); })
      .catch(() => { if (active) setPatientCount(0); });

    testService.getTests({ page: 1, limit: 1 })
      .then((res) => { if (active) setEvaluationCount(res.pagination.total); })
      .catch(() => { if (active) setEvaluationCount(0); });

    // Solo evaluadores/admins activos: es el único listado de usuarios
    // accesible para cualquier rol (GET /users es admin-only).
    userService.getEvaluators()
      .then((evaluators) => { if (active) setActiveUserCount(evaluators.length); })
      .catch(() => { if (active) setActiveUserCount(0); });

    return () => { active = false; };
  }, []);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'evaluator':
        return 'primary';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'evaluator':
        return 'Evaluador';
      case 'viewer':
        return 'Visualizador';
      default:
        return role;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Contenido principal */}
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flex: 1 }}>
        {/* Header del dashboard */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Bienvenido, {user?.firstName} {user?.lastName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={getRoleLabel(user?.role || '')}
              color={getRoleColor(user?.role || '')}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {user?.specialty || 'Sin especialidad'}
            </Typography>
          </Box>
        </Box>

        {/* Tarjetas de estadísticas */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3, 
          mb: 4 
        }}>
          <Card
            component={RouterLink}
            to="/patients"
            sx={{
              height: '100%',
              cursor: 'pointer',
              display: 'block',
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {patientCount === null ? <LoadingOrb size={20} /> : patientCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pacientes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon color="secondary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {evaluationCount === null ? <LoadingOrb size={20} /> : evaluationCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Evaluaciones
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DashboardIcon color="info" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    0
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reportes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {activeUserCount === null ? <LoadingOrb size={20} /> : activeUserCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Usuarios Activos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sección de acciones rápidas */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3 
        }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Acciones Rápidas
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography
                component={RouterLink}
                to="/patients"
                variant="body2"
                color="primary"
                sx={{
                  display: 'block',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                • Agregar nuevo paciente
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Crear evaluación
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Generar reporte
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Ver estadísticas
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Información del Sistema
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Versión:</strong> 1.0.0
              </Typography>
              <Typography variant="body2">
                <strong>Último login:</strong> {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Estado:</strong> {user?.isActive ? 'Activo' : 'Inactivo'}
              </Typography>
              <Typography variant="body2">
                <strong>Email verificado:</strong> {user?.emailVerified ? 'Sí' : 'No'}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default DashboardPage; 